import { isCooler } from '@1delta/lender-registry'
import type { TermAdapter } from './index'

/**
 * Olympus Cooler V2 (MonoCooler) — a treasury-backed CDP the generic pool
 * builder gets wrong on every axis:
 *
 *  - The rate is PROTOCOL-SET (0.5 % effective today, hard-capped at 10 %),
 *    continuously compounded — no utilization curve, no market signal.
 *  - There is NO price-based liquidation. The LTV is a PRICE (debt per gOHM)
 *    that only ratchets UP by code; the sole trigger is accruing debt
 *    outrunning that ratchet, and LLTV = OLTV × 1.01. Zero liquidations ever.
 *  - Seized collateral is unstaked and BURNED — no auction, no liquidator
 *    bonus, no external lender to make whole. The counterparty is the
 *    Olympus treasury itself.
 *  - `minDebtRequired` (1,000 USDS) gates borrows AND partial repays.
 *  - The debt token itself is governance-swappable — read it live.
 */
export const coolerAdapter: TermAdapter = {
  id: 'cooler',
  matches: isCooler,
  profileId: () => 'cooler.mono@v1',
  build: (input) => {
    const c = (input.market?.cooler ?? {}) as Record<string, any>
    return {
      // The "oracle" is a governance drip schedule, not a price feed — the
      // solvency path uses no market price at all.
      oracle: { kind: 'none' },
      supply: {
        role: 'collateral',
        availability: { opensWith: 'both-legs' },
        counterparty: { kind: 'cdp', solvency: 'overcollateralized' },
      },
      borrow: {
        rate: {
          // Governance-set with no curve — the dss/USDD shape.
          kind: 'variable-managed',
          source: 'governance',
          isLocked: false,
          /** MAX_INTEREST_RATE — a hard 10 % cap enforced by code. */
          maxApr: 10,
        },
        debtShape: 'accruing',
        maturity: { kind: 'perpetual' },
        exit: {
          earlyRepay: 'free',
          atMaturityCost: 'accrued',
          lateBehaviour: 'none',
          partialAllowed: true,
          // Over-repay CLAMPS on MonoCooler — a full close is always safe.
          overRepayReverts: false,
        },
        liquidation: {
          // The trigger is TIME, never price: debt compounding at ~0.5 %/yr
          // against an up-only OLTV ratchet (LLTV = OLTV × 1.01). Never apply
          // "HF < 1 at spot price ⇒ liquidatable" copy here.
          trigger: 'time',
          model: 'repay-seize',
          // Seized gOHM is unstaked and BURNED — nobody receives it.
          seizure: 'full-collateral',
          badDebt: 'protocol-absorbed',
          permissioned: false,
        },
        counterparty: {
          kind: 'cdp',
          solvency: 'overcollateralized',
          address:
            typeof c.addresses?.treasury === 'string'
              ? c.addresses.treasury
              : undefined,
        },
        availability: {
          requires: ['token-approval'],
        },
      },
      constraints: {
        crossMargin: false,
        positionModel: 'account',
        positionIdMeaning:
          'One position per address on the monolithic MonoCooler book — the account address is the position key.',
      },
    }
  },
}
